import React, { useState, useEffect, useCallback, useRef } from 'react';
import { InpatientService } from '../../services/inpatientService';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  CircularProgress, 
  Card, 
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Collapse,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BedIcon from '@mui/icons-material/Bed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getUsersForDefaultFacility } from '../../services/facilityUsersService';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../context/AuthContext';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  marginBottom: theme.spacing(2),
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
}));

const CompactTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '& td': {
    padding: '8px 12px',
    fontSize: '0.875rem',
    border: 'none',
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const CompactTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  '& th': {
    padding: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    border: 'none',
  },
}));

const StatusChip = styled(Chip)(({ theme, severity }) => ({
  height: 20,
  fontSize: '0.7rem',
  fontWeight: 500,
  borderRadius: '10px',
  ...(severity === 'warning' && {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  }),
  ...(severity === 'error' && {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  }),
  ...(severity === 'success' && {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  }),
}));

const InpatientList = React.memo(() => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false); // Changed from true to false
  const [searchQuery, setSearchQuery] = useState('');
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  // Get user data including facility information
  const { defaultFacility } = useUserData();
  const { loading: userLoading } = useAuth();
  
  // Use ref to access current facilityUsers without causing dependency issues
  // This prevents fetchInpatients from being recreated every time facilityUsers changes
  const facilityUsersRef = useRef(facilityUsers);
  facilityUsersRef.current = facilityUsers;
  
  // Track if we've already fetched facility users to prevent duplicate calls
  // This prevents the useEffect from running multiple times for the same facility
  const hasFetchedFacilityUsers = useRef(false);
  
  // Track if we're currently fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);
  
  // Track the last processed facility ID to prevent infinite loops
  const lastProcessedFacilityId = useRef(null);

  // Function to calculate age from DOB
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Note: Simplified architecture - removed unused physician functions

  // Function to get initials for avatar
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Function to format date compactly
  const formatDateCompact = (date) => {
    if (!date) return '--';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Function to get days since admission
  const getDaysSinceAdmission = (admitDate) => {
    if (!admitDate) return 0;
    const today = new Date();
    const admit = new Date(admitDate);
    const diffTime = Math.abs(today - admit);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Function to fetch inpatients data (optimized)
  // Uses useRef to access facilityUsers without causing dependency issues
  const fetchInpatients = useCallback(async () => {
    console.log('🔄 InpatientList: fetchInpatients called');
    // Prevent multiple simultaneous calls using ref
    if (isFetchingRef.current) {
      console.log('🔄 InpatientList: Skipping fetch - already fetching');
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    try {
      // Access current values from refs to avoid dependency issues
      const currentDefaultFacility = defaultFacility;
      const currentFacilityUsers = facilityUsersRef.current;
      
      console.log('🔄 InpatientList: Starting fetch for facility:', currentDefaultFacility?.id);
      
      // Only show inpatients for the user's default facility
      if (!currentDefaultFacility?.id) {
        console.log('❌ InpatientList: No defaultFacilityId, setting empty data');
        setRows([]);
        setFilteredRows([]);
        setLoading(false);
        isFetchingRef.current = false;
        return;
      }

      console.log('🔄 InpatientList: Calling InpatientService.getInpatientsByFacility with facilityId:', currentDefaultFacility.id);
      // Use service layer to fetch inpatients
      const encounters = await InpatientService.getInpatientsByFacility(currentDefaultFacility.id, 100);
      console.log('✅ InpatientList: Got encounters from service:', encounters);
      
      // Fetch only essential patient data for each encounter
      const patientIds = encounters.map(enc => enc.patientId);
      console.log('🔄 InpatientList: Fetching patient data for', patientIds.length, 'patients');
      const patients = await InpatientService.getPatientsData(patientIds);
      console.log('✅ InpatientList: Got patient data:', patients);
      
      // Skip facility fetching to reduce data transfer - use default facility name directly
      
      // Skip attending physician fetching to reduce data transfer
      // This saves significant bandwidth by not fetching care team data for each patient
      const attendingPhysicians = encounters.map(() => null);
      
      const data = encounters.map((enc, i) => {
        const patient = patients[i];
        const attendingPhysician = attendingPhysicians[i];
        
        // Get physician name without causing dependency issues
        const getPhysicianNameLocal = (physicianId) => {
          if (!physicianId) return '--';
          const physician = currentFacilityUsers.find(u => u.id === physicianId);
          return physician ? (physician.name || physician.email) : '--';
        };
        
        // Get location name using default facility
        const getLocationName = (enc) => {
          // Use the current default facility name
          const defaultFacilityName = currentDefaultFacility?.name || 'Unknown Facility';
          
          // Combine with unit/department if available
          if (enc.unit) {
            return `${defaultFacilityName} - ${enc.unit}`;
          } else if (enc.department) {
            return `${defaultFacilityName} - ${enc.department}`;
          }
          
          return defaultFacilityName;
        };
        
        // Get patient display name (prioritizes firstName + lastName, falls back to name)
        const getPatientDisplayName = (patient) => {
          if (patient.firstName && patient.lastName) {
            return `${patient.firstName} ${patient.lastName}`;
          } else if (patient.name) {
            return patient.name;
          } else if (patient.firstName) {
            return patient.firstName;
          } else if (patient.lastName) {
            return patient.lastName;
          }
          return 'Unknown';
        };

        return {
          encounterId: enc.id,
          patientId: enc.patientId,
          patientName: getPatientDisplayName(patient),
          mrn: patient.mrn || '--',
          dateOfBirth: patient.dateOfBirth || null,
          age: calculateAge(patient.dateOfBirth),
          sex: patient.gender || '--',
          admitDate: enc.startDate && enc.startDate.toDate ? 
            enc.startDate.toDate() : 
            (enc.startDate ? new Date(enc.startDate) : null),
          bedUnit: getLocationName(enc),
          locationId: enc.locationId,
          originalBedUnit: enc.bedUnit,
          attendingPhysician: attendingPhysician,
          attendingPhysicianName: getPhysicianNameLocal(attendingPhysician),
          diagnosis: enc.diagnosis || enc.reason || '--',
          isolationFlags: enc.isolationFlags || '--',
          status: enc.status || '--',
          notesAlerts: enc.notesAlerts || '--',
          dischargeEta: enc.dischargeEta || '--',
        };
      });
      
      setRows(data);
      setFilteredRows(data);
      console.log('✅ InpatientList: Fetch completed, got', data.length, 'encounters');
    } catch (err) {
      console.error('❌ InpatientList: Error fetching inpatients:', err);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [defaultFacility]); // Include the complete defaultFacility object

  // Single effect to handle both facility users and inpatient data fetching
  // This prevents infinite loops by tracking the last processed facility ID
  useEffect(() => {
    const defaultFacilityId = defaultFacility?.id;
    
    // Prevent infinite loops by checking if we've already processed this facility
    if (defaultFacilityId === lastProcessedFacilityId.current) {
      return;
    }
    
    const initializeData = async () => {
      if (!defaultFacilityId) {
        console.log('❌ InpatientList: No defaultFacilityId available');
        return;
      }

      // Mark this facility as processed to prevent loops
      lastProcessedFacilityId.current = defaultFacilityId;

      // Fetch facility users if needed (only once per facility)
      if (facilityUsers.length === 0 && !hasFetchedFacilityUsers.current) {
        try {
          console.log('🔄 InpatientList: Fetching facility users for facility:', defaultFacilityId);
          hasFetchedFacilityUsers.current = true;
          const users = await getUsersForDefaultFacility(defaultFacilityId);
          console.log('✅ InpatientList: Got', users.length, 'facility users');
          setFacilityUsers(users);
        } catch (error) {
          console.error('❌ InpatientList: Error fetching facility users:', error);
          hasFetchedFacilityUsers.current = false;
        }
      }

      // Fetch inpatient data if not already loading
      if (!loading && !isFetchingRef.current) {
        console.log('✅ InpatientList: Calling fetchInpatients for facility:', defaultFacilityId);
        fetchInpatients();
      }
    };

    initializeData();
  }, [defaultFacility?.id, facilityUsers.length, fetchInpatients, loading]); // Include all dependencies to satisfy React Hook rules

  // Note: Listener functionality removed - use refresh button instead
  // This simplifies the architecture and improves reliability

  // Update filtered rows when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredRows(rows);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = rows.filter(row => {
        const name = (row.patientName || '').toLowerCase();
        const mrn = (row.mrn || '').toLowerCase();
        const bedUnit = (row.bedUnit || '').toLowerCase();
        const diagnosis = (row.diagnosis || '').toLowerCase();
        
        return name.includes(query) || 
               mrn.includes(query) || 
               bedUnit.includes(query) ||
               diagnosis.includes(query);
      });
      setFilteredRows(filtered);
    }
  }, [searchQuery, rows]);

  // Note: Removed duplicate useEffect that was causing infinite loops

  const handleRowClick = (row) => {
    if (expandedRow === row.encounterId) {
      navigate(`/encounters/${row.encounterId}`);
    } else {
      setExpandedRow(expandedRow === row.encounterId ? null : row.encounterId);
    }
  };

  const handleRefresh = async () => {
    try {
      console.log('🔄 InpatientList: Manual refresh triggered');
      await fetchInpatients();
      console.log('✅ InpatientList: Manual refresh completed');
    } catch (error) {
      console.error('❌ InpatientList: Manual refresh failed:', error);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Show loading state if user data is still loading
  if (userLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading user data...</Typography>
      </Box>
    );
  }

  // Debug: log user data to see what's happening (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔄 InpatientList: Component rendered with:', {
      defaultFacility: defaultFacility?.id,
      userLoading,
      rows: rows.length,
      filteredRows: filteredRows.length
    });
  }

  // Show error state if no default facility
  if (!defaultFacility?.id) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            No Default Facility Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your user account does not have a default facility assigned.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact your administrator to set up a default facility.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Debug Info: defaultFacility = {JSON.stringify(defaultFacility)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, background: '#fafafa', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Inpatient Census ({filteredRows.length})
              </Typography>
                             <Typography variant="body2" sx={{ opacity: 0.9 }}>
                 Click row to expand details • Double-click to open encounter • Use refresh button to update data
               </Typography>
            </Box>
            <Tooltip title="Refresh Inpatient Data">
              <span>
                <IconButton 
                  onClick={handleRefresh}
                  disabled={loading}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:disabled': { 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.5)'
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Compact Search */}
      <CompactCard>
        <CardContent sx={{ py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, MRN, bed unit, or diagnosis..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </CardContent>
      </CompactCard>

      {/* Compact Data Table */}
      <CompactCard>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <StyledTableContainer elevation={0}>
              <Table size="small">
                <CompactTableHead>
                  <TableRow>
                    <TableCell sx={{ width: '200px' }}>Patient</TableCell>
                    <TableCell sx={{ width: '80px' }}>Age/Sex</TableCell>
                    <TableCell sx={{ width: '150px' }}>Location</TableCell>
                    <TableCell sx={{ width: '150px' }}>Attending</TableCell>
                    <TableCell sx={{ width: '120px' }}>Admission</TableCell>
                    <TableCell sx={{ width: '100px' }}>Status</TableCell>
                    <TableCell sx={{ width: '40px' }}></TableCell>
                  </TableRow>
                </CompactTableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery ? 'No patients found matching your search.' : 'No inpatients currently admitted.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row, idx) => (
                      <React.Fragment key={row.encounterId || idx}>
                        <CompactTableRow onClick={() => handleRowClick(row)}>
                          <TableCell sx={{ width: '200px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                {getInitials(row.patientName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                  {row.patientName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  MRN: {row.mrn}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '80px' }}>
                            <Box>
                              <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                {row.age !== null ? `${row.age}y` : '--'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.sex}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '150px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <BedIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {row.bedUnit}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '150px' }}>
                            <Typography variant="body2" sx={{ 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {row.attendingPhysicianName}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ width: '120px' }}>
                            <Box>
                              <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                {formatDateCompact(row.admitDate)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getDaysSinceAdmission(row.admitDate)}d ago
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '100px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {row.isolationFlags !== '--' && (
                                <Tooltip title={`Isolation: ${row.isolationFlags}`}>
                                  <span>
                                    <WarningIcon fontSize="small" color="warning" />
                                  </span>
                                </Tooltip>
                              )}
                              {row.notesAlerts !== '--' && (
                                <StatusChip label="Alert" size="small" severity="warning" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: '40px' }}>
                            <IconButton size="small">
                              {expandedRow === row.encounterId ? 
                                <ExpandLessIcon fontSize="small" /> : 
                                <ExpandMoreIcon fontSize="small" />
                              }
                            </IconButton>
                          </TableCell>
                        </CompactTableRow>
                        
                        {/* Expandable Details Row */}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={expandedRow === row.encounterId}>
                              <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <MedicalServicesIcon fontSize="small" />
                                      Medical Information
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>DOB:</strong> {row.dateOfBirth ? new Date(row.dateOfBirth).toLocaleDateString() : '--'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>Diagnosis:</strong> {row.diagnosis}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Status:</strong> {row.status}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AccessTimeIcon fontSize="small" />
                                      Timeline & Alerts
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>Admission:</strong> {row.admitDate ? row.admitDate.toLocaleString() : '--'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>Discharge ETA:</strong> {row.dischargeEta}
                                    </Typography>
                                    {row.notesAlerts !== '--' && (
                                      <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                        <strong>Alerts:</strong> {row.notesAlerts}
                                      </Typography>
                                    )}
                                    {row.isolationFlags !== '--' && (
                                      <Typography variant="body2" sx={{ color: 'error.main' }}>
                                        <strong>Isolation:</strong> {row.isolationFlags}
                                      </Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}
        </CardContent>
      </CompactCard>
    </Box>
  );
});

export default InpatientList; 